import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { useUID } from "react-uid";
import { ExpandCollapse, SplashError, LoadingOverlay } from "@nib-group/chatbot-shared/components";
import { ClientContextType, ChatStatusType } from "@nib-group/chatbot-shared/types";
import { MSG_KEYS, listenForPostMessage, sendPostMessage } from "@nib-group/chatbot-shared/post-message";
import { AnimateFadeOut } from "@nib-group/chatbot-shared/styles";
import { useOriginalDocTitle, usePrevious } from "@nib-group/chatbot-shared/react-utils";
import { manageFlashingDocumentTitle, setFlashingTitleNotification } from "@nib-group/chatbot-shared/document-title";
import { setCachedInlineChatMinimised, removeCachedInlineChatMinimised, getMostRecentPageLink, setMostRecentPageLink, getMostRecentPageName, setMostRecentPageName } from "@nib-group/chatbot-shared/local-storage";
import { waitForClientToLoad, sendClientLaunchConfig, openExternalWindow } from "../../core/load-client-window";
import Toolbar from "./subcomponents/Toolbar";
import CloseChatModal from "./subcomponents/CloseChatModal";
import StatusIconLive from "./subcomponents/StatusIconLive";
import StatusIconLoading from "./subcomponents/StatusIconLoading";
import StatusIconError from "./subcomponents/StatusIconError";
import CONFIG from "./IFrameChatWindow.config";
import CONTENT from "./IFrameChatWindow.content";
import * as S from "./IFrameChatWindow.style";
export const TEST_ID = "Iframe__TestId";
export const IFrameChatWindow = _ref => {
  let {
    url,
    chatConfig,
    startMinimised = false,
    isResuming = false,
    resetChatState
  } = _ref;
  // ----------------------------------------
  // Component state
  // Iframe element ref
  const chatIframe = useRef(null);
  const [canRender, setCanRender] = useState(true);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  // Toggle expand/collapse container and toggle state
  const toggleControlId = useUID();
  // NOTE:
  // - Chat starts maximised when starting
  // - Chat starts minimised when resuming
  // 	- After successfully resumed, THEN it is potentially maximised (if applicable).
  const [isMinimised, setIsMinimised] = useState(isResuming || startMinimised);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const prevUnreadMessages = usePrevious(unreadMessages);
  // Chat iframe is displayed if `isLoading={false}`, otherwise a loading
  // overlay is displayed. Overlay remains present until both:
  // 1. A minimum loading duration has elapsed (`hasMetMinimumLoad`)
  //		- (this is to prevent a quick-loading UI from appearing "jerky" to the user)
  // 2. The contents of the iframe have loaded without incident (`hasClientLoaded`)
  const [isLoading, setIsLoading] = useState(true);
  const [hasMetMinimumLoad, setHasMetMinimumLoad] = useState(false);
  const [hasClientLoaded, setHasClientLoaded] = useState(false);
  // Reflected values passed to us via `postMessage` from the client
  // @ts-ignore
  const [chatStatus, setChatStatus] = useState(undefined);
  const prevChatStatus = usePrevious(chatStatus);
  const [agentName, setAgentName] = useState("");
  const maxLoadTimeout = useRef(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const originalTabTitle = useOriginalDocTitle();
  // ----------------------------------------
  // Variable inferences
  const hasError = hasLoadError || hasTimedOut;
  const showLiveIndicator = chatStatus !== undefined && chatStatus !== ChatStatusType.NotStarted && chatStatus !== ChatStatusType.Ended;
  // ----------------------------------------
  // Methods
  /**
   * Timer to account for excessive load times.
   */
  const startMaxLoadTimer = () => {
    setHasTimedOut(false);
    // Clear any existing timeout
    if (maxLoadTimeout.current) {
      clearTimeout(maxLoadTimeout.current);
      maxLoadTimeout.current = null;
    }
    maxLoadTimeout.current = setTimeout(() => {
      setHasTimedOut(true);
      setIsLoading(false);
    }, CONFIG.IFRAME_LOAD_TIMEOUT);
  };
  /**
   * Timer to account for minimum load time, to prevent janky quick-loading UI.
   */
  const startMinLoadTimer = () => {
    setHasMetMinimumLoad(false);
    setTimeout(() => {
      setHasMetMinimumLoad(true);
    }, CONFIG.MINIMUM_LOADING_TIME);
  };
  /**
   * Check whether inline chat window should be maximised.
   * - On resume, when talking to agent (and not expressly minimised).
   * - On successful transfer, if already minimised.
   */
  useEffect(() => {
    const wasTransferring = prevChatStatus === ChatStatusType.Transferring;
    const isTalkingToAgentNow = chatStatus === ChatStatusType.TalkingToAgent;
    // If minimised (e.g. resuming), but not explicitly so
    // (i.e. the user didn't explicitly minimise the window),
    // and the user is talking to an agent - appropriate to maximise
    const canMaximiseOnResume = !startMinimised && isTalkingToAgentNow && !wasTransferring;
    // On transfer to agent, maximise the window
    const hasJustTransferred = wasTransferring && isTalkingToAgentNow;
    if (canMaximiseOnResume || hasJustTransferred) {
      setIsMinimised(false);
      // Since we've made a judgement call to maximise the chat
      // here, let's reset the cached minimised value, so that
      // any immediate navigation doesn't keep our agent minimised,
      // unless the user explicitly minimised the chat again
      removeCachedInlineChatMinimised();
    }
  }, [chatStatus, prevChatStatus]);
  /**
   * Attach event listeners for the `postMessage` events sent to
   * this window via the client window, regarding chat session stats.
   */
  const listenForChatSessionStats = () => {
    // Receive unread message count updates
    listenForPostMessage({
      msgKey: MSG_KEYS.RECEIVED_MESSAGES_LENGTH,
      onSuccess: (_, payload) => {
        const newMessageCount = payload === null || payload === void 0 ? void 0 : payload.unreadMessages;
        const hasPayload = typeof newMessageCount === "number";
        setUnreadMessages(hasPayload ? newMessageCount : 0);
      }
    });
    // Receive chat status changes
    listenForPostMessage({
      msgKey: MSG_KEYS.CHAT_STATUS_CHANGE,
      onSuccess: (_, payload) => {
        const chatStatusPayload = payload === null || payload === void 0 ? void 0 : payload.chatStatus;
        if (chatStatusPayload) {
          setChatStatus(chatStatusPayload);
          setAgentName(payload === null || payload === void 0 ? void 0 : payload.agentName);
        }
      }
    });
  };
  const resetIFrameState = () => {
    // Reset all relevant state variables to trigger:
    // - iframe condition render and load
    // - accurate loading and error UI behaviour
    // (Comprehensive resets because this logic is shared with "retry" behaviour)
    setIsLoading(true);
    setHasClientLoaded(false);
    setHasMetMinimumLoad(false);
    setHasTimedOut(false);
    setHasLoadError(false);
    if (maxLoadTimeout.current) {
      clearTimeout(maxLoadTimeout.current);
      maxLoadTimeout.current = null;
    }
    // Reset tab title notifications
    setUnreadMessages(0);
    if (originalTabTitle) document.title = originalTabTitle;
    startMinLoadTimer();
    startMaxLoadTimer();
  };
  /**
   * Centralised init function and state variable resets,
   * so it can be shared between init and retry invocations.
   */
  const attemptIframeLoad = () => {
    // Establish event listeners for `postMessage` events sent
    // from the client, which inform us about chat session
    // (this is used for things like updating the Toolbar status text)
    listenForChatSessionStats();
    resetIFrameState();
    // Set the requisite event listeners to the current
    // window so that we can detect when the iframe has loaded
    waitForClientToLoad({
      initialLoadCallback: () => {
        var _a, _b;
        // Don't let the client load timeout anymore
        if (maxLoadTimeout.current) {
          clearTimeout(maxLoadTimeout.current);
          maxLoadTimeout.current = null;
        }
        const hasCustomLaunchConfig = !!chatConfig;
        const iframeWindow = (_b = (_a = chatIframe.current) === null || _a === void 0 ? void 0 : _a.contentWindow) !== null && _b !== void 0 ? _b : null;
        if (iframeWindow && hasCustomLaunchConfig) {
          sendClientLaunchConfig(iframeWindow, chatConfig);
        }
      },
      readyToRenderCallback: () => {
        setHasClientLoaded(true);
      }
    });
  };
  // ----------------------------------------
  // Effects
  // On init, attempt to load provided URL in iframe
  useEffect(attemptIframeLoad, []);
  // Determine when it is appropriate to hide loading UI
  // (Either to show the successfully loaded chat UI, or error UI)
  useEffect(() => {
    if (hasMetMinimumLoad && hasClientLoaded) {
      setIsLoading(false);
    }
  }, [hasMetMinimumLoad, hasClientLoaded]);
  // If there is any error loading the chat iframe, inform
  // the user via document title notification
  useEffect(() => {
    if (hasLoadError || hasTimedOut) {
      setUnreadMessages(unreadMessages + 1);
    }
  }, [hasLoadError, hasTimedOut]);
  // Parity of flashing tab title for unread message notifications
  useEffect(() => {
    const hasChanged = unreadMessages !== prevUnreadMessages;
    if (hasChanged && canRender) {
      if (unreadMessages > 0) {
        setFlashingTitleNotification(unreadMessages, originalTabTitle);
      } else {
        manageFlashingDocumentTitle.stop();
        if (originalTabTitle) document.title = originalTabTitle;
      }
    }
  }, [unreadMessages, prevUnreadMessages]);
  // Notify agent of page navigation changes
  useEffect(() => {
    var _a, _b;
    const isTalkingToAgent = chatStatus === ChatStatusType.TalkingToAgent;
    if (hasClientLoaded) {
      // Necessary to extract these values before the next if block,
      // since they're stored in local storage after the next if block
      const currentPageName = document === null || document === void 0 ? void 0 : document.title;
      const currentPageLink = window.location.href;
      const iframeWindow = (_b = (_a = chatIframe.current) === null || _a === void 0 ? void 0 : _a.contentWindow) !== null && _b !== void 0 ? _b : null;
      if (isResuming && isTalkingToAgent) {
        const mostRecentPageName = getMostRecentPageName();
        const mostRecentPageLink = getMostRecentPageLink();
        // Account for "empty" recent page cache, so that's not a false-positive diff
        const hasValidRecentPage = !!mostRecentPageLink && !!mostRecentPageName;
        // Determine "new page" status by link diff, since document title isn't necessarily unique
        const hasNavigatedToNewPage = currentPageLink !== mostRecentPageLink;
        if (hasValidRecentPage && hasNavigatedToNewPage) {
          // Communicate page navigation message back to the client,
          // so that it can be added to the message history for the agent to see
          sendPostMessage({
            msgKey: MSG_KEYS.PAGE_NAVIGATION_CHANGE,
            targetWindow: iframeWindow,
            payload: {
              pageName: currentPageName,
              pageLink: currentPageLink
            }
          });
        }
      }
      // Regardless of whether the comparison is being made,
      // still store the most recent page name/link in local storage
      setMostRecentPageName(currentPageName);
      setMostRecentPageLink(currentPageLink);
    }
  }, [chatStatus, hasClientLoaded]);
  // ----------------------------------------
  // Event handlers
  const handleOnPopout = () => {
    var _a, _b;
    const {
      height,
      width,
      top,
      left
    } = (_b = (_a = chatIframe.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) !== null && _b !== void 0 ? _b : {};
    setCanRender(false);
    const popOutWindow = openExternalWindow(url, {
      height,
      width,
      top,
      left
    });
    waitForClientToLoad({
      initialLoadCallback: () => {
        // Note it isn't necessary to provide any (optional) custom chat config
        // when opening the pop-out, because pop-out chat sessions already exist.
        // Since we are in effect "resuming" the chat session when opening the
        // pop-out, it is sufficient just to open the window and let the on-load
        // logic in the @client `InitChatContext` pick up the existing session,
        // regardless of whether it was init with a custom config or not.
        // As an additional step, inform the client of the init context: "pop-out"
        // to take any appropriate action, e.g. adding the close window confirm dialog
        sendPostMessage({
          msgKey: MSG_KEYS.INFORM_CLIENT_CONTEXT_CHANGE,
          targetWindow: popOutWindow,
          payload: {
            context: ClientContextType.PopOut
          }
        });
      }
    });
  };
  const handleOnToggleMinimise = () => {
    // In order to reliably set and track the toggled minimised
    // state, we have to use an intermediary variable so we can
    // then cache that variable below
    let toggleMinimiseState;
    setIsMinimised(value => {
      toggleMinimiseState = !value;
      return toggleMinimiseState;
    });
    // In the unlikely even that the setFn hasn't cast
    // our variable to boolean from undefined, fallback
    // to the opposite of whatever the current state is
    setCachedInlineChatMinimised(toggleMinimiseState !== null && toggleMinimiseState !== void 0 ? toggleMinimiseState : !isMinimised);
  };
  // ----------------------------------------
  // Render
  return _jsx(AnimateFadeOut, {
    activate: !canRender,
    unmountWhen: true,
    children: _jsxs(S.Wrapper, {
      children: [_jsx(Toolbar, {
        onToggleClick: handleOnToggleMinimise,
        onPopoutClick:
        // Only allow chat to be popped-out if a `sessionId` has been
        // established. This is necessary so as not to interfere with
        // init-with-config logic and create edge cases.
        hasClientLoaded ? handleOnPopout : undefined,
        onCloseClick: () => {
          setShowConfirmClose(true);
        },
        StatusIconComponent: hasError ? StatusIconError : !hasClientLoaded || chatStatus === ChatStatusType.NotStarted ? StatusIconLoading : showLiveIndicator ? StatusIconLive : null,
        statusText: hasError ? CONTENT.STATUS_TEXT.ERROR : !hasClientLoaded || chatStatus === ChatStatusType.NotStarted ? isResuming ? CONTENT.STATUS_TEXT.RESUMING : CONTENT.STATUS_TEXT.LOADING :
        // If the chat is active, set status text by mapping
        // to the various `ChatStatusType` strings
        chatStatus === ChatStatusType.TalkingToAgent ? CONTENT.STATUS_TEXT.CHAT_STATUS_MSG[ChatStatusType.TalkingToAgent](agentName) : CONTENT.STATUS_TEXT.CHAT_STATUS_MSG[chatStatus],
        unreadMessages,
        toggleControlId,
        isMinimised
      }), _jsx(CloseChatModal, {
        isOpen: showConfirmClose,
        onCancel: () => setShowConfirmClose(false),
        onConfirm: () => {
          var _a, _b;
          const iframeWindow = (_b = (_a = chatIframe.current) === null || _a === void 0 ? void 0 : _a.contentWindow) !== null && _b !== void 0 ? _b : null;
          sendPostMessage({
            msgKey: MSG_KEYS.CLIENT_END_CHAT,
            targetWindow: iframeWindow
          });
          setShowConfirmClose(false);
          setCanRender(false);
          resetChatState();
        }
      }), _jsx(ExpandCollapse, {
        isOpen: !isMinimised,
        id: toggleControlId,
        children: _jsxs(S.IFrameContainer, {
          children: [_jsx(AnimateFadeOut, {
            activate: !isLoading,
            unmountWhen: true,
            children: _jsx(LoadingOverlay, {})
          }), !hasError ? _jsx(S.Iframe, {
            "data-testid": TEST_ID,
            id: TEST_ID,
            ref: chatIframe,
            src: url,
            title: CONTENT.TITLE
          }) : _jsx(SplashError, {
            callback: () => {
              attemptIframeLoad();
              return Promise.resolve();
            }
          })]
        })
      })]
    })
  });
};